import React, { useState, useEffect } from 'react';

/**
 * Premium Choice Card Component  
 * A beautiful, interactive card for presenting user choices with magical animations
 * Built for the "holy sh*t" first-time experience
 */
const ChoiceCard = ({ 
  title, 
  summary, 
  buttonText = "Analyze This", 
  onClick, 
  darkMode, 
  index = 0,
  disabled = false,
  isVisible = true,
  isPreferred = false,
  quickWin = null,
  actionPayload = null,
  showLetterBadge = false,
  // Tutorial-specific metadata
  difficulty = null,
  estimatedTime = null,
  tutorialContext = null
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [hasAnimatedIn, setHasAnimatedIn] = useState(false);

  // Letter badge configuration
  const letterBadgeConfig = {
    0: { letter: 'A', color: 'bg-blue-500' },
    1: { letter: 'B', color: 'bg-purple-500' }, 
    2: { letter: 'C', color: 'bg-green-500' },
    3: { letter: 'D', color: 'bg-orange-500' },
    4: { letter: 'E', color: 'bg-pink-500' }
  };

  // Trigger animation after staggered delay
  useEffect(() => {
    if (isVisible) {
      const delay = index * 200 + 300; // 300ms base delay + 200ms per card
      const timer = setTimeout(() => {
        setHasAnimatedIn(true);
      }, delay);
      
      return () => clearTimeout(timer);
    }
  }, [isVisible, index]);
  
  return (
    <div 
      className={`
        choice-card group relative p-6 rounded-xl cursor-pointer
        transition-all duration-700 ease-out transform-gpu will-change-transform
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        ${!disabled && isHovered ? 'scale-105 -translate-y-3 rotate-1' : 'scale-100 translate-y-0 rotate-0'}
        ${darkMode 
          ? 'bg-gradient-to-br from-gray-800 via-gray-850 to-gray-900' 
          : 'bg-gradient-to-br from-white via-gray-25 to-gray-50'
        }
        border-2 shadow-lg
        ${!disabled && isHovered 
          ? (darkMode 
              ? 'border-purple-400 shadow-2xl shadow-purple-500/20' 
              : 'border-purple-300 shadow-2xl shadow-purple-400/20'
            )
          : isPreferred
            ? (darkMode ? 'border-blue-400 shadow-xl shadow-blue-500/20' : 'border-blue-300 shadow-xl shadow-blue-400/20')
            : (darkMode ? 'border-gray-700' : 'border-gray-200')
        }
        ${hasAnimatedIn ? 'animate-card-entrance' : 'opacity-0 translate-y-12 scale-95'}
        ${isPreferred ? 'ring-2 ring-blue-400/30' : ''}
      `}
      style={{ 
        '--entrance-delay': `${index * 200 + 300}ms`,
        filter: isHovered && !disabled 
          ? 'drop-shadow(0 25px 35px rgba(139, 92, 246, 0.25)) drop-shadow(0 0 0 rgba(255, 255, 255, 0.1))' 
          : 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))',
        transform: isHovered && !disabled 
          ? 'perspective(1000px) rotateX(-2deg) rotateY(1deg)' 
          : 'perspective(1000px) rotateX(0deg) rotateY(0deg)'
      }}
      onMouseEnter={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={disabled ? undefined : onClick}
    >
      {/* Letter Badge - A, B, C */}
      {showLetterBadge && letterBadgeConfig[index] && (
        <div className={`
          absolute -top-3 -left-3 z-10
          w-10 h-10 rounded-full flex items-center justify-center
          text-white font-bold text-lg shadow-lg
          transform transition-all duration-300 ease-out
          ${letterBadgeConfig[index].color}
          ${hasAnimatedIn ? 'scale-100 rotate-0' : 'scale-0 rotate-45'}
          group-hover:scale-110 group-hover:-translate-y-1
        `}
        style={{
          transitionDelay: `${index * 200 + 600}ms` // Appears after card
        }}
        >
          {letterBadgeConfig[index].letter}
        </div>
      )}

      {/* Animated background glow */}
      <div className={`
        absolute -inset-1 rounded-xl -z-10 transition-all duration-700
        ${!disabled && isHovered 
          ? 'bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 opacity-75 blur-md animate-gradient-shift' 
          : 'bg-gradient-to-r from-purple-600/20 to-blue-600/20 opacity-0 blur-sm'
        }
      `} />
      
      {/* Pulsing border effect */}
      <div className={`
        absolute -inset-0.5 rounded-xl -z-10 transition-all duration-500
        ${!disabled && isHovered 
          ? 'bg-gradient-to-r from-purple-400 to-blue-400 opacity-60 animate-pulse' 
          : 'opacity-0'
        }
      `} />
      
      {/* Shimmer sweep effect */}
      <div className={`
        absolute inset-0 rounded-xl overflow-hidden
        ${!disabled && isHovered ? 'opacity-100' : 'opacity-0'}
        transition-opacity duration-300
      `}>
        <div className={`
          absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent
          transform -skew-x-12 transition-transform duration-1000 ease-out
          ${!disabled && isHovered ? 'translate-x-full' : '-translate-x-full'}
        `} />
      </div>
      
      {/* Floating particles effect */}
      <div className={`
        absolute inset-0 rounded-xl overflow-hidden pointer-events-none
        ${!disabled && isHovered ? 'opacity-100' : 'opacity-0'}
        transition-opacity duration-500
      `}>
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className={`
              absolute w-1 h-1 bg-white/40 rounded-full
              ${!disabled && isHovered ? 'animate-float-particle' : ''}
            `}
            style={{
              left: `${20 + i * 12}%`,
              top: `${30 + (i % 2) * 40}%`,
              animationDelay: `${i * 200}ms`,
              animationDuration: '3s'
            }}
          />
        ))}
      </div>
      
      {/* Content - Flex container for consistent layout */}
      <div className="relative z-10 flex flex-col h-full">
        <div className="flex-1">
          <h3 className={`
            text-xl font-bold mb-3 transition-colors duration-300
            ${darkMode 
              ? `text-white ${disabled ? '' : 'group-hover:text-purple-200'}` 
              : `text-gray-900 ${disabled ? '' : 'group-hover:text-purple-700'}`
            }
          `}>
            {title}
          </h3>
          
          <p className={`
            mb-2 leading-relaxed transition-colors duration-300
            ${darkMode 
              ? `text-gray-300 ${disabled ? '' : 'group-hover:text-gray-200'}` 
              : `text-gray-600 ${disabled ? '' : 'group-hover:text-gray-700'}`
            }
          `}>
            {summary}
          </p>

          {/* Page URL - Small and subtle */}
          {actionPayload?.args?.page_url && (
            <div className={`
              mb-4 text-xs transition-colors duration-300 flex items-center gap-1.5
              ${darkMode ? 'text-gray-500' : 'text-gray-400'}
            `}>
              <svg 
                className="w-3 h-3 flex-shrink-0" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" 
                />
              </svg>
              <span className="truncate">{actionPayload.args.page_url}</span>
            </div>
          )}

          {/* Tutorial Metadata - for tutorial cards */}
          {(difficulty || estimatedTime) && (
            <div className="flex flex-wrap gap-2 mb-3">
              {difficulty && (
                <div className={`
                  inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium
                  ${difficulty === 'beginner' 
                    ? (darkMode ? 'bg-green-900/40 text-green-300 border border-green-700/50' : 'bg-green-50 text-green-700 border border-green-200')
                    : difficulty === 'intermediate'
                    ? (darkMode ? 'bg-yellow-900/40 text-yellow-300 border border-yellow-700/50' : 'bg-yellow-50 text-yellow-700 border border-yellow-200')
                    : (darkMode ? 'bg-red-900/40 text-red-300 border border-red-700/50' : 'bg-red-50 text-red-700 border border-red-200')
                  }
                  transition-all duration-300
                `}>
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z"/>
                  </svg>
                  {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                </div>
              )}
              {estimatedTime && (
                <div className={`
                  inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium
                  ${darkMode 
                    ? 'bg-blue-900/40 text-blue-300 border border-blue-700/50' 
                    : 'bg-blue-50 text-blue-700 border border-blue-200'
                  }
                  transition-all duration-300
                `}>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12,6 12,12 16,14"/>
                  </svg>
                  {estimatedTime}
                </div>
              )}
            </div>
          )}

          {/* Quick Win Section - Only on Recommended Choice */}
          {quickWin && (
            <div className={`
              mb-4 p-3 rounded-lg text-xs
              ${darkMode 
                ? 'bg-green-900/20 border border-green-700/30' 
                : 'bg-green-50/80 border border-green-200/50'
              }
            `}>
              <div className={`font-medium mb-2 ${darkMode ? 'text-green-200' : 'text-green-700'}`}>
                🎯 Quick win I spotted:
              </div>
              <div className={`${darkMode ? 'text-green-300' : 'text-green-600'} leading-tight`}>
                {quickWin}
              </div>
            </div>
          )}
        </div>
        
        {/* Button aligned to bottom */}
        <button 
          className={`
            relative w-full py-3 px-6 rounded-lg font-semibold text-white overflow-hidden
            transition-all duration-300 ease-out transform-gpu mt-auto
            ${disabled 
              ? 'bg-gray-500 cursor-not-allowed' 
              : `
                bg-gradient-to-r from-purple-600 via-purple-500 to-blue-600
                hover:from-purple-700 hover:via-purple-600 hover:to-blue-700
                hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/30
                active:scale-95 active:shadow-lg
                focus:outline-none focus:ring-4 focus:ring-purple-300/50
              `
            }
          `}
          disabled={disabled}
        >
          {/* Button glow effect */}
          {!disabled && (
            <div className={`
              absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-400 
              opacity-0 transition-opacity duration-300
              ${isHovered ? 'opacity-20' : ''}
            `} />
          )}
          
          {/* Button shimmer effect */}
          {!disabled && (
            <div className={`
              absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent
              transform -skew-x-12 transition-transform duration-700 ease-out
              ${isHovered ? 'translate-x-full' : '-translate-x-full'}
            `} />
          )}
          
          <span className="relative flex items-center justify-center gap-2">
            {buttonText}
            <svg 
              className={`
                w-4 h-4 transition-all duration-300 ease-out
                ${disabled ? '' : (isHovered ? 'translate-x-1 scale-110' : '')}
              `} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M13 7l5 5m0 0l-5 5m5-5H6" 
              />
            </svg>
          </span>
        </button>
      </div>
    </div>
  );
};

// Custom CSS animations for the Choice Card
export const ChoiceCardStyles = () => (
  <style jsx global>{`
    @keyframes card-entrance {
      0% {
        opacity: 0;
        transform: translateY(40px) scale(0.9) rotateX(10deg);
      }
      60% {
        opacity: 0.8;
        transform: translateY(-8px) scale(1.02) rotateX(-2deg);
      }
      100% {
        opacity: 1;
        transform: translateY(0) scale(1) rotateX(0deg);
      }
    }
    
    @keyframes gradient-shift {
      0%, 100% {
        background-position: 0% 50%;
      }
      50% {
        background-position: 100% 50%;
      }
    }
    
    @keyframes float-particle {
      0%, 100% {
        transform: translateY(0px) rotate(0deg);
        opacity: 0.4;
      }
      50% {
        transform: translateY(-20px) rotate(180deg);
        opacity: 0.8;
      }
    }
    
    .animate-card-entrance {
      animation: card-entrance 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    }
    
    .animate-gradient-shift {
      background-size: 200% 200%;
      animation: gradient-shift 3s ease infinite;
    }
    
    .animate-float-particle {
      animation: float-particle 3s ease-in-out infinite;
    }
    
    /* Enhanced hover state isolation */
    .choice-card {
      contain: layout style paint;
      isolation: isolate;
    }
    
    /* Custom gradients for better color stops */
    .via-gray-850 {
      --tw-gradient-to: rgb(31 31 41 / var(--tw-gradient-to-opacity, 1));
      --tw-gradient-stops: var(--tw-gradient-from), rgb(55 55 65 / var(--tw-gradient-via-opacity, 1)), var(--tw-gradient-to);
    }
    
    .via-gray-25 {
      --tw-gradient-to: rgb(249 250 251 / var(--tw-gradient-to-opacity, 1));
      --tw-gradient-stops: var(--tw-gradient-from), rgb(253 253 253 / var(--tw-gradient-via-opacity, 1)), var(--tw-gradient-to);
    }
    
    /* Performance optimizations */
    .choice-card * {
      backface-visibility: hidden;
      -webkit-backface-visibility: hidden;
    }
  `}</style>
);

export default ChoiceCard;
